"use client";

import Script from "next/script";
import React, { Fragment, type JSX, useEffect, useRef } from "react";

import { SSOSignInProps } from "components/sso-sign-in/sso-sign-in.props";

import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";

// Window augmentation — extends the SSO/Salesforce-related globals already
// declared in `types/salesforceInteractions.d.ts` with the SSO-specific
// callbacks injected by the bootstrap script below.
declare global {
  interface Window {
    triggerLogOut?: () => Promise<void>;
    ecolabExperienceLibrary?: {
      init: (config: {
        getTokenCallback?: Window["generateToken"];
        authorizeCallback?: Window["authorize"];
        signOutCallback?: Window["triggerLogOut"];
        useSelfAuthorization: boolean;
        isWaffleEnabled: boolean;
      }) => void;
    };
  }
}

const SSO_BOOTSTRAP_SOURCE = `
(function() {
  if (window.SSOFunctionsInitialized) return;

  function addQueryStringToUrls(apiUrl, queryStringName, queryStringValue) {
    const separator = apiUrl.indexOf('?') !== -1 ? '&' : '?';
    return apiUrl + separator + queryStringName + '=' + queryStringValue;
  }

  function addMcPIdToUrls(apiUrl) {
    const mcpIdQueryStringName = 'mcpid';
    if (
      typeof window.SalesforceInteractions !== 'undefined' &&
      window.SalesforceInteractions !== null &&
      window.SalesforceInteractions?.getAnonymousId &&
      typeof window.SalesforceInteractions.getAnonymousId === 'function'
    ) {
      const anonymousId = window.SalesforceInteractions.getAnonymousId();
      if (anonymousId && anonymousId !== 'undefined' && anonymousId !== '') {
        apiUrl = addQueryStringToUrls(apiUrl, mcpIdQueryStringName, anonymousId);
      }
    }
    return apiUrl;
  }

  async function fetchJson(url) {
    try {
      const res = await fetch(url, {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store',
          'Pragma': 'no-cache'
        }
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[SSO] fetchJson non-OK:', res.status, res.statusText, text);
        return null;
      }
      return await res.json().catch(err => {
        console.error('[SSO] fetchJson parse error:', err);
        return null;
      });
    } catch (err) {
      console.error('[SSO] fetchJson network error:', err);
      return null;
    }
  }

  window.triggerLogOut = async function () {
  sessionStorage.removeItem('ssoLoggedIn');
    try {
      const data = await fetchJson('/api/auth/logout');
      if (data && data.signOutUrl) {
        window.location.href = data.signOutUrl;
      } else {
        console.error('[SSO] Logout: missing signOutUrl or fetch failed:', data);
      }
    } catch (e) {
      console.error('[SSO] Logout unexpected error:', e);
    }
  };

  window.authorize = async function () {
  sessionStorage.setItem('ssoLoggedIn', 'true');
    try {
      const data = await fetchJson(addMcPIdToUrls('/api/auth/get-signin-url'));
      if (data && data.signInUrl) {
        window.location.href = data.signInUrl;
      } else {
        console.error('[SSO] authorize: missing signInUrl or fetch failed:', data);
      }
    } catch (e) {
      console.error('[SSO] authorize unexpected error:', e);
    }
  };

  window.generateToken = async function (tokenType, scope) {
    try {
      let apiUrl = '/api/auth/generate-token';
      if (tokenType && tokenType.trim() !== '') apiUrl = addQueryStringToUrls(apiUrl, 'tokenType', tokenType);
      if (scope && scope.trim() !== '') apiUrl = addQueryStringToUrls(apiUrl, 'scope', scope);
      apiUrl = addMcPIdToUrls(apiUrl);
      const data = await fetchJson(apiUrl);
      return (data && data.tokenData) ? data.tokenData : '';
    } catch (e) {
      console.error('[SSO] generateToken unexpected error:', e);
      return '';
    }
  };

  window.SSOFunctionsInitialized = true;

  document.dispatchEvent(
    new CustomEvent('HOSTISREADY', {
      detail: { autoSignIn: false }
    })
  );
})();
`;

export const Default = (props: SSOSignInProps): JSX.Element => {
  const { page, fields } = props;
  const isPageEditing = page.mode.isEditing;
  const [placeholderType, setPlaceholderType] = React.useState<
    "guest" | "user" | null
  >(null);
  const initStartedRef = useRef(false);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const stored = sessionStorage.getItem("ssoLoggedIn");
      setPlaceholderType(stored === "true" ? "user" : "guest");
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const enableSSOSignIn =
    (process.env.NEXT_PUBLIC_ENABLE_SSO_SIGNIN || "true") === "true";

  const placeholderMarkup = fields?.PlaceholdersMarkup?.value;
  const javascriptLink = fields?.JavascriptLink?.value;
  const customerSignInUrl = fields?.CustomerSignInUrl?.value?.href;

  // Run the SSO bootstrap exactly once on the client. Previously this used
  // `document.createElement('script')` inside the render function, which
  // ran every render. Now it lives in a one-shot effect and the placeholder
  // markup renders unconditionally so layout is stable from first paint.
  useEffect(() => {
    if (!enableSSOSignIn || isPageEditing) return;
    if (!placeholderMarkup || !javascriptLink) return;
    if (window.SSOFunctionsInitialized || initStartedRef.current) return;

    initStartedRef.current = true;

    const script = document.createElement("script");
    script.textContent = SSO_BOOTSTRAP_SOURCE;
    document.head.appendChild(script);
  }, [enableSSOSignIn, isPageEditing, placeholderMarkup, javascriptLink]);

  useEffect(() => {
  if (typeof window === "undefined") return;
  const container = document.querySelector('.sso-sign-in-container');
  if (!container) return;
  const observer = new MutationObserver(() => {
    const actualWidget = container.querySelector(
      '.ecolab-global-header-widget .user-info'
    );

    if (actualWidget) {
      console.log('Actual SSO loaded');
      const wrapper = document.querySelector('.sso-placeholder-wrapper');
      wrapper?.remove();

      console.log('Actual SSO loaded');

      // Remove tabindex from the outermost widget
      actualWidget?.removeAttribute('tabindex');
      actualWidget?.removeAttribute('role');
      observer.disconnect();
    }
  });

  observer.observe(container, {
    childList: true,
    subtree: true,
  });

  return () => observer.disconnect();
}, []);

  //Add alt text to user icon for accessibility
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const icon = document.querySelector(".user-icon");
      if (icon && !icon.getAttribute("alt")) {
        icon.setAttribute("alt", "User Icon");
        observer.disconnect(); // stop once done
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleScriptLoad = () => {
    if (!enableSSOSignIn) return;
    try {
      const lib = Function(
        'return typeof ecolabExperienceLibrary !== "undefined" && ecolabExperienceLibrary',
      )();
      if (!lib) {
        console.error(
          "[SSO] ecolabExperienceLibrary not accessible at runtime",
        );
        return;
      }
      window.ecolabExperienceLibrary = lib;
      lib.init({
        getTokenCallback: window.generateToken,
        authorizeCallback: window.authorize,
        signOutCallback: window.triggerLogOut,
        useSelfAuthorization:
          (process.env.ECOLAB_SSO_USE_SELF_AUTHORIZATION || "false") === "true",
        isWaffleEnabled:
          (process.env.ECOLAB_SSO_WAFFLE_ENABLED || "true") === "true",
      });
    } catch (e) {
      console.error("[SSO] Failed to init ecolabExperienceLibrary:", e);
    }
  };

  const handleScriptError = () => {
    console.error("Failed to load external script", {
      scriptUrl: javascriptLink,
    });
  };

  // Editor mode placeholder
  if (isPageEditing) {
    return (
      <Fragment>
        <span>SSO Sign In</span>
      </Fragment>
    );
  }

  // SSO enabled and Sitecore provided the markup + script — render both.
  // Container renders on SSR + client first paint so layout height is reserved.
  if (enableSSOSignIn && placeholderMarkup && javascriptLink) {
    return (
      <Fragment>
        <Script
          src={javascriptLink}
          strategy="afterInteractive"
          onLoad={handleScriptLoad}
          onError={handleScriptError}
        />
         <div className="sso-placeholder-wrapper h-[40px] flex items-center">

  {placeholderType === 'guest' ? (
    <div
      className="custom-sso-placeholder"
      style={{
        display: 'flex',
        gap: '8px',
        marginTop: '4px',
        marginLeft: '10px',
      }}
    >
      <Icon icon={ICONS.USERICON} className="user-icon" />

      <label className="sign-in-label">
        Sign In
      </label>
    </div>
  ) : placeholderType === 'user' ? (
    <div
      className="logged-in-placeholder"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '22px',
        marginLeft: '12px',
        marginTop: '4px',
      }}
    >
      <div>
        <Icon icon={ICONS.APP_DRAWER} className="app-drawer" />
      </div>

      <div className="circular-div"></div>
    </div>
  ) : null}

</div>
        <div
          className="sso-sign-in-container h-[40px]"
          dangerouslySetInnerHTML={{ __html: placeholderMarkup }}
          suppressHydrationWarning
        />
      </Fragment>
    );
  }

  // Fallback Customer Sign-In link when SSO is disabled
  if (!enableSSOSignIn && customerSignInUrl) {
    return (
      <div
        className="customer-sign-in-container min-h-[40px] max-lg:pb-[25px] max-lg:w-full"
        data-role="customer-signin"
      >
        <div>
          <a
            href={customerSignInUrl}
            className="lpselect w-auto flex items-center gap-2 xl:px-[16px] xl:pb-[11px] xl:pt-[15px] cursor-pointer font-normal hover:underline active:underline focus:underline xl:active:no-underline xl:focus:no-underline xl:hover:no-underline xl:hover:bg-[var(--hover-effect-opacity)]"
          >
            <span className="leading-[17.6px] text-base font-normal">
              {fields?.CustomerSignInUrl?.value?.text || "Sign In"}
            </span>
            <Icon
              icon={ICONS.CHEVRON_WHITE_RIGHT}
              className="block lg:hidden w-4 h-4 ml-2"
            />
          </a>
        </div>
      </div>
    );
  }

  return <></>;
};
